import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import React, { FC, useState } from "react";
import { useTheme } from "@mui/material/styles";
import useResponsive from "../../hooks/useResponsive";
import { JobIdDetailsProps } from "../../interface/job_board";
import { formatDate } from "../../utils/url-id";
import { Dot } from "../UIElements/svgIcons";
import DotDropDown from "../UIElements/dot-dropdown";
import { JobEmpStatus } from "./jobTable";
import { useAuth } from "../../hooks/useAuth";
import { useNotificationService } from "../notification";
import { useMutation } from "@tanstack/react-query";
import { apiUpdateJobStatus } from "../../api/job";
import ModalContainer from "../UIElements/modal-container";
import SelectInput from "../UIElements/select";
import { LoadingButton } from "@mui/lab";
import { DeleteJob } from "./modal-content";
import { textSty } from "../UIElements/styles-input";

const JobHeading: FC<{
  jobDetails: JobIdDetailsProps;
  loading: boolean;
  modalStatus: string;
  setModalStatus: React.Dispatch<React.SetStateAction<string>>;
  jobId: string;
}> = ({ jobDetails, loading, jobId, modalStatus, setModalStatus }) => {
  const isMedium = useResponsive("down", "md", "xl");
  const [open, setOpen] = useState(false);
  const handleModal = () => setOpen(!open);

  const [open_delete, setOpen_delete] = useState(false);
  const handleDeleteModal = () => setOpen_delete(!open_delete);

  const headerSty = {
    fontFamily: "DM Sans",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "18px",
    letterSpacing: "-0.23999999463558197px",
    textAlign: "left",
    color: "#404E57",
    marginBottom: 1,
  };

  
  return (
    <>
      <JobUpdateModal
        open={open}
        handleModal={handleModal}
        jobId={jobId}
        modalStatus={modalStatus}
        setModalStatus={setModalStatus}
      />
      <DeleteJob open={open_delete} modalHandler={handleDeleteModal} />
      <Box>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          {!loading ? (
            <Stack
              sx={{ px: 2, width: "100%" }}
              direction="row"
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h3" sx={{ color: "#000", fontWeight: 700 }}>
                {jobDetails.job_title}
              </Typography>

            </Stack>
          ) : (
            <Skeleton
              variant="rounded"
              width={"70%"}
              height={30}
              sx={{ px: 3, ml: 3 }}
            />
          )}
        </Stack>
        {!loading ? (
          <Stack direction={"row"} sx={{ flexWrap: "wrap", px: 2 }}>
            <Stack direction="row" sx={{ gap: 1, mx: 1 }}>
              <Typography sx={{ ...headerSty }}>Department: </Typography>
              <Typography sx={{ ...textSty }}>
                {jobDetails?.department}
              </Typography>
            </Stack>
            {!isMedium && <Typography sx={{ ...textSty, mx: 2 }}>|</Typography>}
            <Stack direction="row" sx={{ gap: 1, mx: 1 }}>
              <Typography sx={{ ...headerSty }}>Job Type: </Typography>
              <Typography sx={{ ...textSty }}>
                {jobDetails?.job_type}
              </Typography>
            </Stack>
            {!isMedium && <Typography sx={{ ...textSty, mx: 2 }}>|</Typography>}
            <Stack direction="row" sx={{ gap: 1, mx: 1 }}>
              <Typography sx={{ ...headerSty }}>Posting Date:</Typography>
              <Typography sx={{ ...textSty }}>
                {formatDate(jobDetails?.createdAt)}{" "}
              </Typography>
            </Stack>
            {!isMedium && <Typography sx={{ ...textSty, mx: 2 }}>|</Typography>}
            <Stack direction="row" sx={{ gap: 1 }}>
              <Typography sx={{ ...headerSty }}>Status:</Typography>
              <JobEmpStatus status={modalStatus ?? ""} />
            </Stack>
          </Stack>
        ) : (
          <Skeleton
            variant="rounded"
            width={"75%"}
            height={20}
            sx={{ px: 3, my: 2, ml: 3 }}
          />
        )}
      </Box>
    </>
  );
};

export default JobHeading;

const JobUpdateModal: FC<{
  open: boolean;
  jobId: string;
  handleModal: () => void;
  modalStatus: string;
  setModalStatus: React.Dispatch<React.SetStateAction<string>>;
}> = ({ open, handleModal, jobId, modalStatus, setModalStatus }) => {
  const { token } = useAuth();
  const { showNotification } = useNotificationService();

  const mutation = useMutation({
    mutationFn: (fieldValues: {
      status: string;
      jobId: string;
      token: string;
    }) => apiUpdateJobStatus(fieldValues),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        showNotification(data?.data?.message ?? "Status Updated");
        handleModal();
      }
    },
    onError: (error) => {
      showNotification("Fetch process failed", "error");
    },
  });
  const handleStatus = () => {
    const form = {
      status: modalStatus,
      jobId: jobId,
      token,
    };
    mutation.mutate(form);
  };
  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"xs"}
      openModal={open}
      fullWidth={true}
      handleModal={handleModal}
    >
      <Box
        sx={{
          background: "#fff",
          padding: 3,
          borderRadius: "16px",
        }}
      >
        <Stack>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Outfit",
              fontWeight: 500,
              color: "#292925",
              marginBottom: 3,
            }}
          >
            Update job status
          </Typography>
          <SelectInput
            label={"Select Status"}
            value={modalStatus}
            onChange={(e) => setModalStatus(e.target.value)}
            options={[
              { value: "active", label: "Active" },
              { value: "pending", label: "Pending" },
              { value: "closed", label: "Closed" },
            ]}
          />
          <Stack direction="row" spacing={3} justifyContent={"flex-end"}>
            <Button
              variant="outlined"
              onClick={handleModal}
              sx={{ borderRadius: "8px" }}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={mutation.isPending}
              onClick={handleStatus}
              sx={{ borderRadius: "6px" }}
            >
              Update
            </LoadingButton>
          </Stack>
        </Stack>
      </Box>
    </ModalContainer>
  );
};
